import { gql as apolloGql } from '@apollo/client';
import { useApolloClient, useQuery, useSubscription } from '@apollo/client/react';
import { Box, Button, Center, Heading, Icon, Spacer, Spinner, StackProps, Text, VStack } from '@chakra-ui/react';
import { ChatMessage, COMMENT_EMBEDS_SELECTION } from '@src/components/sco/Chat/ChatMessage';
import { COMMENT_THINKING_SELECTION } from '@src/components/sco/Chat/ChatThinkingPanel';
import { COMMENT_STORED_FILES_SELECTION } from '@src/components/sco/Comments/CommentAttachments';
import { ScoComment } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { LuArrowDown } from 'react-icons/lu';
import { PiArrowFatLinesUpBold } from 'react-icons/pi';

// Apollo cache.writeFragment needs a real FragmentDefinition. The fraql `gql`
// from @src/lib/gql converts pure fragment documents to InlineFragment, which
// makes writeFragment throw "Found 0 fragments".
const SCO_COMMENT_LIVE_PATCH_FRAGMENT = apolloGql`
  fragment ScoCommentLivePatch on ScoComment {
    id
    commentId
    body
    embedsAsJson
    createdAt
    deletedAt
    thinking {
      status
      text
      startedAt
      completedAt
      truncated
    }
  }
`;
export type ChatMessageListProps = {
  blankState?: any;
  containerStackProps?: StackProps;
  inlineReplies?: boolean;
  isInline?: boolean;
  onHasMessages?: (hasMessages: boolean) => void;
  parentCommentId?: string;
  refetchTrigger?: any;
  showParentCommentFirst?: boolean;
  threadId: string;
};
export const ChatMessageList = (props: ChatMessageListProps) => {
  const apolloClient = useApolloClient();
  const [isOlderLoading, setIsOlderLoading] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messageListStartRef = useRef<HTMLDivElement>(null);
  const messageListEndRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const hasScrolledToBottomRef = useRef(false);
  const pageSize = 20;
  const variables = {
    page: 1,
    pageSize,
    order: [['createdAt', 'DESC']],
    parentCommentId: props.parentCommentId,
    threadId: props.threadId,
    collectionId: `ChatThread:${props.threadId}:${props.parentCommentId || 0}:comments`
  };
  const commentSubscription = useSubscription(gql`
      subscription ($threadId: String!, $parentCommentId: String) {
        scoThreadComment(threadId: $threadId, parentCommentId: $parentCommentId) {
          id
          key
          threadId
          commentId
          thread {
            id
            lastActiveAt
          }
          comment {
            id
            commentId
            body
            ${COMMENT_THINKING_SELECTION}
            ${COMMENT_EMBEDS_SELECTION}
            createdAt
            deletedAt
            ${COMMENT_STORED_FILES_SELECTION}
          }
        }
      }
    `, {
    variables: {
      threadId: props.threadId,
      parentCommentId: props.parentCommentId
    },
    onData: ({
      data: subData
    }) => {
      // Merge live comment fields (especially embedsAsJson) into the cache.
      // fetchMore only pulls *newer* rows and would leave AI answer locks stale.
      const live = subData.data?.scoThreadComment?.comment;
      if (!live?.id) {
        return;
      }
      try {
        apolloClient.cache.writeFragment({
          id: apolloClient.cache.identify({
            __typename: 'ScoComment',
            id: live.id
          }),
          fragment: SCO_COMMENT_LIVE_PATCH_FRAGMENT,
          data: {
            __typename: 'ScoComment',
            id: live.id,
            commentId: live.commentId,
            body: live.body,
            embedsAsJson: live.embedsAsJson,
            createdAt: live.createdAt,
            deletedAt: live.deletedAt,
            thinking: live.thinking ?? null
          }
        });
      } catch (e) {
        // Non-fatal — fetchMore path still runs
        console.warn('[ChatMessageList] live comment cache write failed', e);
      }
    }
  });
  const query = useQuery(gql`
    query(
      $collectionId: String,
      $beforeCreatedAt: DateTime
      $afterCreatedAt: DateTime
      $order: [[String]]
      $page: Int
      $pageSize: Int
      $parentCommentId: String
      $threadId: String
    ) {
      forum {
        id
        thread(threadId: $threadId) {
          id
          commentsCount
        }
        comments(
          collectionId: $collectionId
          order: $order
          pageSize: $pageSize
          page: $page
          parentCommentId: $parentCommentId
          beforeCreatedAt: $beforeCreatedAt
          afterCreatedAt: $afterCreatedAt
          threadId: $threadId
        ) {
          id
          pagination {
            itemCount
          }
          items {
            id
            ${ChatMessage.fragments.ScoComment()}
          }

        }
      }
    }
  `, {
    variables
  });
  const parentCommentQuery = useQuery(gql`
      query ($commentId: String!) {
        forum {
          comment(commentId: $commentId) {
            id
            ${ChatMessage.fragments.ScoComment()}
          }
        }
      }
    `, {
    variables: {
      commentId: props.parentCommentId
    },
    skip: !props.showParentCommentFirst
  });
  const queryDataItems = query.data?.forum?.comments?.items || [];
  const comments: ScoComment[] = [...queryDataItems].sort((a, b) => {
    return moment(a.createdAt).diff(moment(b.createdAt));
  });
  const newestCommentShown = comments[comments.length - 1];
  const oldestCommentShown = comments[0];
  const totalCommentsCount = query.data?.forum?.thread?.commentsCount || 0;
  useEffect(() => {
    props.onHasMessages?.(totalCommentsCount > 0 || comments.length > 0);
  }, [comments.length, props.onHasMessages, totalCommentsCount]);

  // When scoThreadComment fires (or refetchTrigger bumps), pull newer messages.
  // Do not gate on isRegistered — guest tool-chat (SignupRequestChatIntake) relies on the
  // same subscription + fetchMore path; typing indicators already work over WS for guests.
  useEffect(() => {
    if (!refetchTrigger || !props.threadId) {
      return;
    }
    query.fetchMore({
      variables: {
        ...variables,
        afterCreatedAt: newestCommentShown?.createdAt
      }
    }).catch(err => console.error(err));
  }, [refetchTrigger, props.threadId, commentSubscription?.data?.scoThreadComment]);
  useEffect(() => {
    setRefetchTrigger(x => x + 1);
  }, [props.refetchTrigger]);
  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    isAtBottomRef.current = true;
  }, [props.threadId]);
  const handleScroll = () => {
    if (messageListRef.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = messageListRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 100;
      isAtBottomRef.current = atBottom;
    }
  };
  useEffect(() => {
    if (props.isInline || !messageListEndRef.current) {
      return;
    }
    if (!hasScrolledToBottomRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'instant',
        // @ts-ignore
        container: 'nearest'
      });
      if (!query.loading) {
        hasScrolledToBottomRef.current = true;
      }
    } else if (isAtBottomRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        // @ts-ignore
        container: 'nearest'
      });
    }
  }, [newestCommentShown?.commentId, props.isInline, query.loading]);
  useEffect(() => {
    if (props.isInline || !messageListRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (isAtBottomRef.current && hasScrolledToBottomRef.current) {
        messageListEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          // @ts-ignore
          container: 'nearest'
        });
      }
    });
    resizeObserver.observe(messageListRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [props.isInline]);
  useEffect(() => {
    if (props.isInline || !messageListEndRef.current) {
      return;
    }
    if (isAtBottomRef.current && hasScrolledToBottomRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        // @ts-ignore
        container: 'nearest'
      });
    }
  }, [newestCommentShown?.body, newestCommentShown?.thinking?.text, props.isInline]);
  return <>
      {query.loading && !query.data && <Center padding={'1em'}>
          <Spinner />
        </Center>}
      {!props.parentCommentId && comments.length == 0 && totalCommentsCount == 0 && !query.loading && <>
          {props.blankState ? <>{props.blankState}</> : <Center height={'full'} paddingY={'2em'}>
              <VStack>
                <Heading color={'colorPalette.fg/60'} size={'lg'} textAlign={'center'}>
                  No messages yet.
                </Heading>
                <Text color={'colorPalette.fg/60'} textAlign={'center'}>
                  Start by saying hello!
                </Text>
                <Icon asChild fontSize={'xl'} marginTop={4}>
                  <LuArrowDown />
                </Icon>
              </VStack>
            </Center>}
        </>}
      <VStack ref={messageListRef} align={'stretch'} gap={0} width={'full'} onScroll={handleScroll} {...props.isInline ? {} : {
      flexGrow: 1,
      overflowY: 'auto'
    }} {...props.containerStackProps}>
        <Box ref={messageListStartRef} />
        {props.parentCommentId && props.showParentCommentFirst && parentCommentQuery.data?.forum?.comment && <ChatMessage comment={parentCommentQuery.data.forum.comment} />}
        {oldestCommentShown && totalCommentsCount > comments.length ? <Center padding={'1em'}>
            <Button disabled={isOlderLoading} size={'sm'} variant={'outline'} onClick={() => {
          setIsOlderLoading(true);
          query.fetchMore({
            variables: {
              ...variables,
              afterCreatedAt: null,
              beforeCreatedAt: oldestCommentShown.createdAt
            }
          }).then(() => {
            setIsOlderLoading(false);
          });
        }}>
              {isOlderLoading ? <Spinner size={'xs'} /> : <>
                  <Icon asChild color={'colorPalette.fg/50'}>
                    <PiArrowFatLinesUpBold />
                  </Icon>
                  <Text marginX={'1em'} textStyle={'labelXs'}>
                    Show Older Messages
                  </Text>
                  <Icon asChild color={'colorPalette.fg/50'}>
                    <PiArrowFatLinesUpBold />
                  </Icon>
                </>}
            </Button>
          </Center> : <>
            <Spacer />
          </>}

        {comments.map((comment, idx) => <ChatMessage key={comment.commentId} comment={comment} inlineReplies={props.inlineReplies} nextComment={comments[idx + 1]} priorComment={comments[idx - 1]} />)}

        {/*<SoftForceAnswerBanner comments={comments} />*/}

        <Box ref={messageListEndRef} />
      </VStack>
    </>;
};